import React, { useState, useEffect } from 'react'
import LoaderComponent from '../../components/LoaderComponent'
import DemoVideoRequestPrompt from './DemoVideoRequest'
import { useReduxAuth } from '../../../auth/useReduxAuth'

interface DemoVideo {
  id: string
  title: string
  description: string
  thumbnail: string
}

const DemoVideosPage: React.FC = () => {
  const [videos, setVideos] = useState<DemoVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');


  const { user } = useReduxAuth()
  const userName = user?.full_name || 'User'

  const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;
  const CHANNEL_ID = process.env.REACT_APP_YOUTUBE_CHANNEL_ID;
  

  useEffect(() => {
    const fetchVideos = async () => {

      try {
        const params = new URLSearchParams({
          key: API_KEY || '',
          channelId: CHANNEL_ID || '',
          part: 'snippet,id',
          order: 'date',
          maxResults: '50',
        });
        const url = `https://www.googleapis.com/youtube/v3/search?${params.toString()}`;

        const res = await fetch(url);
        const data = await res.json();

        if (data.error) throw new Error(data.error.message)

        // 🔹 Flatten the structure
        const formatted = data.items
          .filter((item: any) => item.id.kind === 'youtube#video')
          .map((item: any) => ({
            id: item.id.videoId,
            title: item.snippet.title,
            description: item.snippet.description,
            thumbnail: item.snippet.thumbnails.medium.url,
          }))

        setVideos(formatted)
      } catch (err: any) {
        console.error(err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchVideos()
  }, [API_KEY, CHANNEL_ID])

  if (loading) return <LoaderComponent />
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className='container-fluid pt-3' style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <h1 className='fw-bold' style={{ letterSpacing: '1px' }}>Demo Videos</h1>
      <DemoVideoRequestPrompt userName={userName} />
      <div className="d-flex flex-column gap-4">
        {videos.map((video) => (
          <div
            key={video.id}
            className="w-100 d-flex"
          >
            <div
              className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-3 p-4 rounded shadow-sm bg-white video-card flex-grow-1"
              style={{
                width: '100%',
                transition: 'box-shadow 0.2s',
                minHeight: 180,
                border: '1px solid #f0f0f0'
              }}
            >
              {/* Thumbnail */}
              <a
                href={`https://www.youtube.com/watch?v=${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="position-relative overflow-hidden rounded shadow-sm flex-shrink-0"
                style={{ width: '100%', maxWidth: '240px', height: '135px', display: 'inline-block' }}
                onMouseEnter={(e) => {
                  const iframe = e.currentTarget.querySelector('iframe') as HTMLIFrameElement;
                  iframe.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  const iframe = e.currentTarget.querySelector('iframe') as HTMLIFrameElement;
                  iframe.style.opacity = '0';
                }}
              >
                {/* Thumbnail Image */}
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-100 h-100 rounded position-absolute top-0 start-0"
                  style={{
                    objectFit: 'cover',
                    transition: 'opacity 0.3s ease-in-out, transform 0.2s',
                  }}
                />

                {/* Iframe (hidden until hover) */}
                <iframe
                  src={`https://www.youtube.com/embed/${video.id}?autoplay=1&mute=1&controls=0&showinfo=0&loop=1&playlist=${video.id}`}
                  allow="autoplay"
                  title={`Demo video preview - ${video.title}`} 
                  className="position-absolute top-0 start-0 w-100 h-100 border-0 rounded"
                  style={{
                    opacity: 0,
                    transition: 'opacity 0.3s ease-in-out',
                    pointerEvents: 'none',
                  }}
                />
              </a>


              {/* Video Info */}
              <div className="ms-md-4 flex-grow-1">
                <a
                  href={`https://www.youtube.com/watch?v=${video.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  <h4 className="mb-2 text-primary fw-semibold" style={{ lineHeight: 1.3 }}>
                    {video.title}
                  </h4>
                </a>
                <p className="mb-2 text-muted" style={{ fontSize: '1.05rem' }}>
                  {video.description}
                </p>
                <div className="d-flex align-items-center mt-3">
                  <i className="bi bi-youtube text-danger fs-4 me-2"></i>
                  <span className="fw-semibold text-dark">CandiHealth</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default DemoVideosPage
